/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router'
import { WINDOW } from '@myprofile-ui/pidx-utils'
import { paths } from '../pages/pages.paths'

@Injectable({
  providedIn: 'root',
})
export class RedirectGuardService {
  private window = inject<Window>(WINDOW)
  private router = inject(Router)

  static readonly DEFAULT_PATH = paths.personalData
  static readonly DEFAULT_DISTANCE_THRESHOLD = 7
  static readonly MIN_DISTANCE_THRESHOLD = 3

  // the maximum length delta between the original path and correct path
  private static getThreshold(path: string): number {
    if (path.length < this.DEFAULT_DISTANCE_THRESHOLD) {
      return this.MIN_DISTANCE_THRESHOLD
    }

    return this.DEFAULT_DISTANCE_THRESHOLD
  }

  // The actual implementation of the levenshtein distance algorithm
  private static levenshtein(a: string, b: string): number {
    if (a.length === 0) {
      return b.length
    }
    if (b.length === 0) {
      return a.length
    }

    const matrix = []

    // increment along the first column of each row
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i]
    }

    // increment each column in the first row
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j
    }

    // Fill in the rest of the matrix
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1, // deletion
          )
        }
      }
    }

    return matrix[b.length][a.length]
  }

  // Once the levenshtein algorithm has done its magic, we have to sort the results
  private static sortByDistances(typoPath: string, dictionary: string[]) {
    const pathsDistance = {} as { [name: string]: number }

    dictionary.sort((a, b) => {
      if (!(a in pathsDistance)) {
        pathsDistance[a] = RedirectGuardService.levenshtein(a, typoPath)
      }
      if (!(b in pathsDistance)) {
        pathsDistance[b] = RedirectGuardService.levenshtein(b, typoPath)
      }

      return pathsDistance[a] - pathsDistance[b]
    })
  }

  public calculateClosestRoutePath(url: string, possiblePaths: { [name: string]: string }): string {
    const regex = /.*\/(?<country>\w{2})\/(?<locale>\w{2}([-_])\w{2})\/(?<pathname>.*)/
    const matches = url.match(regex)

    const country = this.getNormalizedCountry(matches?.groups?.country ?? '')
    const locale = this.getNormalizedLocale(matches?.groups?.locale ?? '')
    const pathname = matches?.groups?.pathname

    // If no pathname is present or the url is malformed we redirect to the default path
    if (!pathname) {
      if (!country || !locale) {
        const [browserLanguage, browserCountry] = this.window.navigator.language.split('-')
        return `${browserCountry}/${browserLanguage}_${browserCountry}/${RedirectGuardService.DEFAULT_PATH}`
      }

      return `${country}/${locale}/${RedirectGuardService.DEFAULT_PATH}`
    }

    const threshold = RedirectGuardService.getThreshold(pathname)
    const dictionary = Object.values(possiblePaths).filter((path) => Math.abs(path.length - pathname.length) <= threshold)

    if (!dictionary.length) {
      return `${country}/${locale}/${RedirectGuardService.DEFAULT_PATH}`
    }

    RedirectGuardService.sortByDistances(pathname, dictionary)
    return `${country}/${locale}/${dictionary[0]}`
  }

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const newUrl = this.calculateClosestRoutePath(state.url, paths)
    return this.router.parseUrl(newUrl)
  }

  private getNormalizedCountry(country: string): string | undefined {
    return country?.toLowerCase()
  }

  private getNormalizedLocale(locale: string): string | undefined {
    if (locale == null) return undefined
    const [l1, l2] = locale.includes('-') ? locale.split('-') : locale.split('_')
    return `${l1.toLowerCase()}_${l2?.toUpperCase()}`
  }
}
